import { useCallback, useEffect, useRef, useState } from 'react';

import {
	deactivateMonthlyAccountSplit,
	fetchMonthlyAllocations,
	MonthlyAllocationCellUpdate,
	MonthlyAllocationGrid,
	MonthlyAllocationGridRow,
	saveMonthlyAllocations,
	ApiResponse,
	FetchParams,
} from '../services/api';

// Types

export interface UseMonthlyAllocationsParams {
	isEnabled?: boolean;
	spendPlanId?: number;
	onSaveComplete?: () => void;
}

export interface UseMonthlyAllocationsReturn {
	data: MonthlyAllocationGrid | null;
	error: string | null;
	loading: boolean;
	saving: boolean;
	refetch: () => Promise<void>;
	updateCell: (
		activityId: number,
		month: string,
		amount: number,
		contractId?: number | null,
		accountId?: number | null
	) => void;
	saveAll: () => Promise<void>;
	deactivateAccountSplit: (
		activityId: number,
		contractId: number | null,
		accountId: number
	) => Promise<void>;
}

// Hook

const AUTO_SAVE_DELAY = 1500; // ms

/** Composite key for a grid row: activity + optional contract + optional account */
const rowKey = (activityId: number, contractId?: number | null, accountId?: number | null) =>
	`${activityId}:${contractId ?? 'null'}:${accountId ?? 'null'}`;

export const useMonthlyAllocations = ({
	isEnabled = true,
	spendPlanId,
	onSaveComplete,
}: UseMonthlyAllocationsParams = {}): UseMonthlyAllocationsReturn => {
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	const [data, setData] = useState<MonthlyAllocationGrid | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);

	// Track pending changes for auto-save
	const pendingRef = useRef<Map<string, MonthlyAllocationCellUpdate>>(new Map());
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// =========================================================================
	// Fetch
	// =========================================================================

	const fetchData = useCallback(async () => {
		if (!accessToken || !idToken || !spendPlanId) return;
		setLoading(true);
		setError(null);
		try {
			const params: FetchParams = { accessToken, idToken };
			const res: ApiResponse<MonthlyAllocationGrid> = await fetchMonthlyAllocations(
				params,
				spendPlanId
			);
			if (res.error) {
				setError(res.error.message);
			} else {
				setData(res.data ?? null);
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to fetch monthly allocations');
		} finally {
			setLoading(false);
		}
	}, [accessToken, idToken, spendPlanId]);

	useEffect(() => {
		if (isEnabled && spendPlanId) {
			fetchData();
		}
	}, [isEnabled, fetchData, spendPlanId]);

	// =========================================================================
	// Save
	// =========================================================================

	const flush = useCallback(async () => {
		if (!accessToken || !idToken || !spendPlanId) return;
		const pending = Array.from(pendingRef.current.values());
		if (pending.length === 0) return;

		// Clear pending before sending
		pendingRef.current.clear();

		setSaving(true);
		try {
			const params: FetchParams = { accessToken, idToken };
			const res = await saveMonthlyAllocations(params, spendPlanId, pending);
			if (res.error) {
				setError(res.error.message);
			} else {
				// Skip setData here — optimistic updates in updateCell already reflect
				// the correct local state. Replacing data with the server response would
				// create a new object reference, causing the entire grid to re-render
				// (hierarchy → rows → every DataGrid cell), disrupting user input.
				onSaveComplete?.();
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to save monthly allocations');
		} finally {
			setSaving(false);
		}
	}, [accessToken, idToken, spendPlanId, onSaveComplete]);

	// =========================================================================
	// Cell update — optimistic update + debounced auto-save
	// =========================================================================

	const updateCell = useCallback(
		(
			activityId: number,
			month: string,
			amount: number,
			contractId?: number | null,
			accountId?: number | null
		) => {
			// Optimistic local update
			setData((prev) => {
				if (!prev) return prev;
				const rk = rowKey(activityId, contractId, accountId);
				const rows: MonthlyAllocationGridRow[] = prev.rows.map((row) => {
					if (rowKey(row.activityId, row.contractId, row.accountId) !== rk) return row;
					const newMonths = { ...row.months, [month]: amount };
					const newTotal = Object.values(newMonths).reduce((sum, v) => sum + v, 0);
					return {
						...row,
						months: newMonths,
						total: newTotal,
						...(accountId !== undefined ? { accountId } : {}),
					};
				});
				// Recompute column totals
				const columnTotals: Record<string, number> = {};
				let grandTotal = 0;
				for (const row of rows) {
					for (const [m, amt] of Object.entries(row.months)) {
						columnTotals[m] = (columnTotals[m] || 0) + amt;
						grandTotal += amt;
					}
				}
				return { ...prev, rows, columnTotals, grandTotal };
			});

			// Queue for auto-save
			const key = `${activityId}:${contractId ?? 'null'}:${accountId ?? 'null'}:${month}`;
			pendingRef.current.set(key, {
				activityId,
				month,
				amount,
				...(contractId !== undefined ? { contractId } : {}),
				...(accountId !== undefined ? { accountId } : {}),
			});

			// Debounce
			if (timerRef.current) clearTimeout(timerRef.current);
			timerRef.current = setTimeout(() => {
				flush();
			}, AUTO_SAVE_DELAY);
		},
		[flush]
	);

	// Save-all (explicit trigger)
	const saveAll = useCallback(async () => {
		if (timerRef.current) clearTimeout(timerRef.current);
		await flush();
	}, [flush]);

	// Cleanup timer on unmount
	useEffect(() => {
		return () => {
			if (timerRef.current) clearTimeout(timerRef.current);
		};
	}, []);

	const refetch = useCallback(async () => {
		await fetchData();
	}, [fetchData]);

	/** Deactivate an account split (soft-deletes all monthly rows for that account combo) */
	const deactivateAccountSplit = useCallback(
		async (activityId: number, contractId: number | null, accountId: number) => {
			if (!accessToken || !idToken || !spendPlanId) return;
			// Flush any pending changes first
			if (timerRef.current) clearTimeout(timerRef.current);
			await flush();

			setSaving(true);
			try {
				const params: FetchParams = { accessToken, idToken };
				const res = await deactivateMonthlyAccountSplit(
					params,
					spendPlanId,
					activityId,
					contractId,
					accountId
				);
				if (res.error) {
					setError(res.error.message);
				} else {
					setData(res.data ?? null);
					onSaveComplete?.();
				}
			} catch (err) {
				setError(err instanceof Error ? err.message : 'Failed to deactivate account split');
			} finally {
				setSaving(false);
			}
		},
		[accessToken, idToken, spendPlanId, flush, onSaveComplete]
	);

	return { data, error, loading, saving, refetch, updateCell, saveAll, deactivateAccountSplit };
};
